import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { fetchDashboard, type DashboardPayload } from "@/api";

/**
 * Shared dashboard data hook used by all pages.
 * Returns the dashboard payload, loading/error state, and tenant filter helpers.
 * Tenant filter is URL-driven via ?tenant=X (single source of truth).
 */
export function useDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTenant = searchParams.get("tenant");

  const setSelectedTenant = (slug: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (slug) next.set("tenant", slug);
    else next.delete("tenant");
    setSearchParams(next, { replace: true });
  };

  const query = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => fetchDashboard(),
    refetchInterval: 60_000,
  });

  return {
    ...query,
    selectedTenant,
    setSelectedTenant,
  };
}

/**
 * Filtered subset of dashboard payload for a specific tenant.
 * Returns null if no tenant selected or no data yet.
 */
export function useTenantSlice(data: DashboardPayload | undefined, tenant: string | null) {
  const [slice, setSlice] = useState<{
    client: DashboardPayload["clients"][number] | undefined;
    contracts: DashboardPayload["contracts"];
    pending_inputs: DashboardPayload["pending_inputs"];
    pending_approvals: DashboardPayload["pending_approvals"];
    open_requests: DashboardPayload["open_requests"];
    health_scores: DashboardPayload["health_scores"];
  } | null>(null);

  useEffect(() => {
    if (!data || !tenant) {
      setSlice(null);
      return;
    }
    setSlice({
      client: data.clients.find((c) => c.slug === tenant),
      contracts: data.contracts.filter((c) => c.tenant_id === tenant),
      pending_inputs: data.pending_inputs.filter((i) => i.tenant_id === tenant),
      pending_approvals: data.pending_approvals.filter((a) => a.tenant_id === tenant),
      open_requests: data.open_requests.filter((r) => r.tenant_id === tenant),
      health_scores: data.health_scores.filter((h) => h.tenant_id === tenant),
    });
  }, [data, tenant]);

  return slice;
}
